/* eslint-disable react/display-name */
"use client";

import { Icons } from "@/components/icons";
import {
  Dialog,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { User } from "@/lib/auth";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React from "react";
import { MenuIcons, UserDropdown } from "./components";
import { XIcon } from "lucide-react";
import { useDebounce } from "react-use";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/axios";
import { EventType } from "@/lib/types";
import Link from "next/link";
import { isPast } from "date-fns";
import Image from "next/image";
import { motion } from "framer-motion";
import { useSidebarState } from "./sidebar";
import ThemeToggle from "@/components/ui/theme-toggle";
import { useEffect, useState } from "react";
import { Typewriter } from 'react-simple-typewriter';


export function SearchBar({ user }: { user: User | null }) {

  const { isOpen } = useSidebarState();

  return (
    <div className="bg-[#171B20] dark:bg-white fixed inset-x-0 top-0 bg-opacity-50 dark:bg-opacity-50 backdrop-blur dark:backdrop-blur firefox:bg-opacity-90 z-20 lg:z-30 pl-0 ml-0 lg:pl-[70px] pr-0 lg:pr-[70px]">
      <div className='hidden lg:block absolute top-[32px] left-[46px] z-20'>
        <MenuIcons />
      </div>
      <div className="absolute hidden lg:block dark:hidden top-4 left-[130px]">
        <Link href="/" className="pointer-events-auto">
          <Image
            src="/logo.png"
            className="object-contain mx-auto"
            width={98}
            height={44}
            alt="Mastermind logo"
          />
        </Link>
      </div>
      <div className="absolute hidden lg:hidden dark:lg:block top-4 left-[130px]">
        <Link href="/" className="pointer-events-auto">
          <Image
            src="/logo-dark.svg"
            className="object-contain mx-auto"
            width={98}
            height={44}
            alt="Mastermind logo"
          />
        </Link>
      </div>
      <motion.div
        className="max-w-[1120px] mx-auto flex items-center justify-between h-20"
        animate={{
          x: isOpen ? "9.75rem" : "0rem",
        }}
        transition={{
          ease: "easeOut",
        }}
      >
        <div className="hidden lg:block w-[12.5rem]" />
        <div className="flex items-center justify-center w-full py-4 pr-4 mx-auto lg:space-x-4">
          <SearchDialog>
            <div className="relative hidden w-full rounded-full text-theme-gray lg:block lg:max-w-xl">

              <div className='absolute top-1/2 -translate-y-1/2 left-10 text-sm'>
                <Typewriter
                  words={['Amazing Muharram', 'Amazing Quran', 'Amazing Parenting']}
                  loop={0} // 0 means infinite loop
                  cursor
                  cursorStyle="_"
                  typeSpeed={100}
                  deleteSpeed={50}
                  delaySpeed={2000}
                />
              </div>

              <Input
                className="w-full h-10 pl-10 border-none rounded-full pointer-events-none bg-theme-black2 dark:bg-theme-dark_card3"
                placeholder=''
              />

              <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                <Icons.search className="w-4 h-4 text-current" />
              </div>
            </div>
          </SearchDialog>
          <div className="flex items-center flex-1 lg:flex-grow-0 lg:hidden">
            <div className="absolute inset-0 flex dark:hidden items-center justify-start px-4 w-full h-20 pointer-events-none relaive">
              <Link href="/" className="pointer-events-auto">
                <Image
                  src="/logo.png"
                  className="object-contain mx-auto"
                  width={98}
                  height={44}
                  alt="Mastermind logo"
                />
              </Link>
            </div>
            <div className="absolute inset-0 hidden dark:flex items-center justify-start px-4 w-full h-20 pointer-events-none relaive">
              <Link href="/" className="pointer-events-auto">
                <Image
                  src="/logo-dark.svg"
                  className="object-contain mx-auto"
                  width={98}
                  height={44}
                  alt="Mastermind logo"
                />
              </Link>
            </div>
          </div>
          <ThemeToggle />
        </div>
        <UserDropdown initialData={user} />
      </motion.div>
    </div>
  );
}

export function SearchDialog({ children }: { children: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        placement="top"
        className="max-w-[1120px] mx-auto w-full p-0 [&_.x-close]:hidden"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Search Events</DialogTitle>
        </DialogHeader>

        <SeachResult />
      </DialogContent>
    </Dialog>
  );
}

function SeachResult() {
  const [val, setVal] = React.useState("");
  const [debouncedValue, setDebouncedValue] = React.useState("");

  const [, cancel] = useDebounce(
    () => {
      setDebouncedValue(val);
    },
    500,
    [val]
  );

  const { data, isSuccess, isFetching } = useQuery(
    ["events", "search", debouncedValue],
    async () => {
      const response = await axiosInstance.get<EventType[]>(
        "/api/events/search?" +
        new URLSearchParams({
          q: debouncedValue,
        }).toString()
      );

      return response.data;
    },
    {
      enabled: debouncedValue.trim() !== "",
    }
  );

  return (
    <div className="w-full p-1 space-y-4">
      <div className="relative w-full max-w-xl mx-auto rounded-full text-theme-gray bg-theme-black2 dark:bg-[#DBDFE4]">
        <Input
          className="w-full h-10 pl-10 border-none rounded-full bg-theme-black2 dark:outline-none dark:bg-[#DBDFE4]"
          placeholder="Cari event untuk kamu ikuti disini..."
          onChange={(e) => {
            setVal(e.target.value);
          }}
          value={val}
        />

        <div className="absolute inset-y-0 left-0 flex items-center pl-4">
          {isFetching ? (
            <Icons.spinner className="w-4 h-4 text-current animate-spin" />
          ) : (
            <Icons.search className="w-4 h-4 text-current" />
          )}
        </div>
      </div>

      <div className="overflow-hidden overflow-y-auto w-full max-w-xl mx-auto rounded-[20px] p-3 max-h-[190px] bg-theme-black2 dark:bg-[#DBDFE4] text-theme-gray mt-6">
        {isSuccess
          ? data.map((event) => (
            <Link
              key={event.id}
              href={`/${event.slug}`}
              className="flex items-center p-3 focus-visible:bg-white/10 focus:outline-none"
            >
              {isPast(new Date(event.end_date)) ? (
                <Icons.clock className="w-5 h-5 mr-2 shrink-0 text-theme-accent" />
              ) : (
                <Icons.hot className="w-5 h-5 mr-2 shrink-0 text-[#C62660]" />
              )}
              <span>{event.title}</span>
            </Link>
          ))
          : null}
      </div>
    </div>
  );
}

type DialogComponentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  placement?: "top" | "bottom" | "center";
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogComponentProps
>(({ className, children, placement = "center", ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay
      className={cn("place-items-center p-4", {
        "items-end": placement === "bottom",
        "items-start": placement === "top",
        "items-center": placement === "center",
      })}
    >
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "w-full gap-4 relative px-6 py-12 text-white border-gray-800 animate-in data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 sm:rounded-lg sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="x-close absolute right-4 top-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <XIcon className="w-6 h-6 text-theme-gray" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogOverlay>
  </DialogPortal>
));
